import React, { useState, useEffect, useMemo } from "react"
import Select from "react-select"

const OPERATOR_OPTIONS = [
  { id: "NO_SELECT", name: "NO SELECT" },
  { id: "OR", name: "OR" },
  { id: "NOT_OR", name: "NOT OR" },
]

const SurveyCustomInput = ({
  questionDropdown = [],
  optionDropdown = [],
  attrs = {},
  value = [],
  operator = null,
  disabled = false,
  onChange,
  onOperatorChange,
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState([])
  // Internal state for operator to ensure UI updates
  const [internalOperator, setInternalOperator] = useState(operator)

  const isQuestionMultiple = attrs?.questionMultiple ?? false
  const isAnswerMultiple = attrs?.answerMultiple ?? false

  // Sync internal state with prop
  useEffect(() => {
    setInternalOperator(operator)
  }, [operator])

  // Get current operator value - convert null to "NO_SELECT" for display
  const operatorValue = useMemo(() => {
    const operatorId = internalOperator === null ? "NO_SELECT" : internalOperator
    const found = OPERATOR_OPTIONS.find(opt => opt.id === operatorId)
    return found || OPERATOR_OPTIONS[0]
  }, [internalOperator])

  // Build question options for the first dropdown
  const questionOptions = useMemo(() => {
    return questionDropdown.map(q => ({
      value: String(q.id),
      label: q.name || q.label || q.questionText,
      ...q,
    }))
  }, [questionDropdown])

  // Build a flat map of all answers with their questionId
  // API structure: optionDropdown[].options[] contains the answers
  const allAnswersFlat = useMemo(() => {
    const answers = []
    optionDropdown.forEach(question => {
      const questionId = String(question.id)
      const questionOptions = question.options || []
      questionOptions.forEach(opt => {
        answers.push({
          value: String(opt.id),
          label: opt.name || opt.label,
          questionId: questionId,
          questionName: question.name,
        })
      })
    })
    return answers
  }, [optionDropdown])

  // Build answer options based on selected questions
  const answerOptions = useMemo(() => {
    const validQuestions = selectedQuestions.filter(q => q.value !== null)
    if (validQuestions.length === 0) {
      return []
    }

    const selectedQuestionIds = validQuestions.map(q => String(q.value))

    // Filter answers for selected questions
    return allAnswersFlat.filter(answer =>
      selectedQuestionIds.includes(answer.questionId)
    )
  }, [selectedQuestions, allAnswersFlat])

  // Derive selected answers from value prop (array of answer IDs)
  const selectedAnswers = useMemo(() => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return isAnswerMultiple ? [] : null
    }

    const selected = answerOptions.filter(opt =>
      value.includes(String(opt.value)) || value.includes(opt.value)
    )

    if (isAnswerMultiple) {
      return selected.length > 0 ? selected : []
    }
    return selected[0] || null
  }, [value, answerOptions, isAnswerMultiple])

  // Derive display value for question dropdown
  const questionDisplayValue = useMemo(() => {
    if (isQuestionMultiple) {
      return selectedQuestions.filter(q => q.value !== null)
    }
    const validQuestion = selectedQuestions.find(q => q.value !== null)
    return validQuestion || null
  }, [selectedQuestions, isQuestionMultiple])

  // Initialize selectedQuestions from value on mount/value change
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      // Find which questions contain the selected answers
      const questionIdsWithSelectedAnswers = new Set()

      allAnswersFlat.forEach(answer => {
        if (value.includes(String(answer.value)) || value.includes(answer.value)) {
          questionIdsWithSelectedAnswers.add(answer.questionId)
        }
      })

      const questionsToSelect = questionOptions.filter(q =>
        questionIdsWithSelectedAnswers.has(String(q.value))
      )

      if (questionsToSelect.length > 0) {
        setSelectedQuestions(questionsToSelect)
      }
    }
  }, []) // Only run on mount

  // Handle operator change
  const handleOperatorChange = selected => {
    // Convert "NO_SELECT" back to null for storage
    const newOperator = selected?.id === "NO_SELECT" ? null : (selected?.id ?? null)
    
    // Update internal state immediately for UI
    setInternalOperator(newOperator)
    
    // Notify parent
    onOperatorChange(newOperator)
    
    // Clear values if NO SELECT
    if (newOperator === null) {
      setSelectedQuestions([])
      onChange([])
    }
  }

  // Handle question selection change
  const handleQuestionChange = selected => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      setSelectedQuestions([])
      onChange([])
      return
    }

    const newSelected = isQuestionMultiple
      ? selected || []
      : [selected]

    setSelectedQuestions(newSelected)

    // Clear answers that no longer belong to selected questions
    if (value && Array.isArray(value) && value.length > 0) {
      const newQuestionIds = newSelected.map(q => String(q.value))
      const validAnswerIds = value.filter(answerId => {
        const answer = allAnswersFlat.find(
          a => String(a.value) === String(answerId)
        )
        return answer && newQuestionIds.includes(answer.questionId)
      })

      if (validAnswerIds.length !== value.length) {
        onChange(validAnswerIds)
      }
    }
  }

  // Handle answer selection change
  const handleAnswerChange = selected => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      onChange([])
      return
    }

    const selectedArray = isAnswerMultiple ? selected : [selected]
    const answerIds = selectedArray.map(item => String(item.value))
    onChange(answerIds)
  }

  // Check if operator is selected (not null)
  const isOperatorSelected = internalOperator !== null

  return (
    <>
      <div className="col-md-3">
        <Select
          className="select2-selection"
          value={operatorValue}
          onChange={handleOperatorChange}
          getOptionValue={option => option.id}
          getOptionLabel={option => option.name}
          options={OPERATOR_OPTIONS}
          isDisabled={disabled}
        />
      </div>
      {isOperatorSelected && (
        <>
          <div className="col-md-3">
            <Select
              className="select2-selection"
              value={questionDisplayValue}
              onChange={handleQuestionChange}
              getOptionValue={option => option.value}
              getOptionLabel={option => option.label}
              options={questionOptions}
              isMulti={isQuestionMultiple}
              isClearable
              isDisabled={disabled}
              placeholder="NO SELECT"
            />
          </div>
          <div className="col-md-3">
            <Select
              className="select2-selection"
              value={selectedAnswers}
              onChange={handleAnswerChange}
              getOptionValue={option => option.value}
              getOptionLabel={option => option.label}
              options={answerOptions}
              isMulti={isAnswerMultiple}
              isClearable
              isDisabled={disabled}
              placeholder="NO SELECT"
            />
          </div>
        </>
      )}
    </>
  )
}

export default SurveyCustomInput
